import React, { useEffect, useMemo, useState } from "react";
import globalAxios from "axios";
import { FiChevronDown, FiChevronRight, FiLayers, FiList, FiRefreshCw, FiUploadCloud } from "react-icons/fi";
import { useDeploymentSummary, type DeploymentSummary, type DeploymentSummaryGroup } from "./useDeploymentSummary";

/** One-button promote: push everything staged in preview to production. */
export const usePromoteToProduction = (onDone: () => void) => {
	const [running, setRunning] = useState(false);
	const [result, setResult] = useState<string | null>(null);
	const promote = async () => {
		setRunning(true);
		setResult(null);
		try {
			// Relative URL → app's shared API base (REACT_APP_API_URL in dev).
			const res = await globalAxios.post<{ total: number; succeeded: number; failed: number }>(
				`/api/deployment/promote`, null,
				{ withCredentials: true, params: { target: "production" } }
			);
			const d = res.data ?? { total: 0, succeeded: 0, failed: 0 };
			setResult(d.failed > 0
				? `Promoted ${d.succeeded}/${d.total} — ${d.failed} failed.`
				: `Promoted ${d.succeeded} item(s) to production.`);
			onDone();
		} catch {
			setResult("Promotion failed. See server logs.");
		} finally {
			setRunning(false);
		}
	};
	return { promote, running, result };
};

/** Colour for a reason/action badge — same palette as the rest of the deployment page. */
const badgeClass = (bucket: string): string => {
	const b = bucket.toLowerCase();
	if (b === "new" || b === "created") return "bg-emerald-50 text-emerald-700 border-emerald-200";
	if (b === "deleted" || b === "removed") return "bg-rose-50 text-rose-700 border-rose-200";
	if (b === "modified" || b === "updated") return "bg-amber-50 text-amber-700 border-amber-200";
	return "bg-slate-50 text-slate-600 border-slate-200";
};

const GroupList: React.FC<{ title: string; icon: React.ReactNode; groups: DeploymentSummaryGroup[] }> = ({ title, icon, groups }) => {
	const [open, setOpen] = useState<Record<string, boolean>>({});
	if (groups.length === 0) {
		return (
			<div className="mt-3">
				<div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{icon}{title}</div>
				<p className="mt-1 text-sm text-slate-400">Nothing pending.</p>
			</div>
		);
	}
	return (
		<div className="mt-3">
			<div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{icon}{title}</div>
			<ul className="mt-2 space-y-1">
				{groups.map((g) => {
					const isOpen = !!open[g.key];
					const buckets = Object.entries(g.breakdown);
					return (
						<li key={g.key} className="rounded-lg border border-slate-200 bg-white">
							<button
								type="button"
								onClick={() => setOpen((o) => ({ ...o, [g.key]: !o[g.key] }))}
								className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50"
							>
								<span className="flex items-center gap-2 min-w-0">
									{isOpen ? <FiChevronDown className="shrink-0 text-slate-400" /> : <FiChevronRight className="shrink-0 text-slate-400" />}
									<span className="truncate text-sm font-medium text-slate-800">{g.label}</span>
								</span>
								<span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700">{g.total}</span>
							</button>
							{isOpen && buckets.length > 0 && (
								<div className="flex flex-wrap gap-1.5 border-t border-slate-100 px-3 py-2">
									{buckets.map(([bucket, n]) => (
										<span key={bucket} className={`rounded-full border px-2 py-0.5 text-xs font-medium ${badgeClass(bucket)}`}>
											{bucket}: {n}
										</span>
									))}
								</div>
							)}
						</li>
					);
				})}
			</ul>
		</div>
	);
};

const Stat: React.FC<{ label: string; value: number; accent?: boolean }> = ({ label, value, accent }) => (
	<div className={`rounded-xl border px-3 py-2 ${accent ? "border-orange-200 bg-orange-50" : "border-slate-200 bg-slate-50"}`}>
		<div className={`text-lg font-bold ${accent ? "text-orange-700" : "text-slate-800"}`}>{value}</div>
		<div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</div>
	</div>
);

const TargetColumn: React.FC<{ target: "preview" | "production"; refreshTrigger?: number }> = ({ target, refreshTrigger }) => {
	const { summary, loading, error, load } = useDeploymentSummary(target);
	useEffect(() => { load(refreshTrigger); }, [load, refreshTrigger]);
	const { promote, running: promoting, result: promoteResult } = usePromoteToProduction(() => load(Date.now()));

	const s: DeploymentSummary = summary;
	const isProd = target === "production";
	const nothingToPromote = s.totals.outOfSyncPages + s.totals.outOfSyncSingletons + s.totals.queued === 0;
	return (
		<div className={`rounded-2xl border bg-white p-4 ${isProd ? "border-orange-200" : "border-slate-200"}`}>
			<div className="flex items-center justify-between">
				<h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">{isProd ? "Production" : "Preview"}</h3>
				<button type="button" onClick={() => load(Date.now())} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600" title="Refresh">
					<FiRefreshCw className={loading ? "animate-spin" : ""} /> Refresh
				</button>
			</div>
			{error ? (
				<p className="mt-3 text-sm text-rose-600">{error}</p>
			) : (
				<>
					<div className="mt-3 grid grid-cols-3 gap-2">
						<Stat label="Pages" value={s.totals.outOfSyncPages} accent={isProd} />
						<Stat label="Singletons" value={s.totals.outOfSyncSingletons} />
						<Stat label="Queued" value={s.totals.queued} />
					</div>
					{isProd && (
						<div className="mt-3">
							<button
								type="button"
								onClick={promote}
								disabled={promoting || nothingToPromote}
								className="flex w-full items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-slate-300"
								title={nothingToPromote ? "Nothing staged to promote" : "Push everything staged in preview to production"}
							>
								<FiUploadCloud className={promoting ? "animate-pulse" : ""} />
								{promoting ? "Promoting…" : "Promote to Production"}
							</button>
							{promoteResult && <p className="mt-2 text-center text-xs text-slate-500">{promoteResult}</p>}
						</div>
					)}
					<GroupList title="Pages by section" icon={<FiLayers className="text-slate-400" />} groups={s.pageGroups} />
					<GroupList title="Queue by content type" icon={<FiList className="text-slate-400" />} groups={s.queueGroups} />
				</>
			)}
		</div>
	);
};

/**
 * Grouped "difference from target" overview — the clear, collapsible replacement for
 * the confusing flat thousands-row list + dual counters. Additive band above the
 * existing deployment panels; consumes GET /api/deployment/summary for each target.
 */
const DeploymentOverview: React.FC<{ previewRefresh?: number; productionRefresh?: number }> = ({ previewRefresh, productionRefresh }) => {
	const columns = useMemo(() => ["preview", "production"] as const, []);
	return (
		<div className="shrink-0 grid grid-cols-2 gap-4 px-4 pt-4">
			{columns.map((t) => (
				<TargetColumn key={t} target={t} refreshTrigger={t === "preview" ? previewRefresh : productionRefresh} />
			))}
		</div>
	);
};

export default DeploymentOverview;
